import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Progress } from "../ui/progress";
import { Badge } from "../ui/badge";
import { Play, CheckCircle2, Circle, Rocket } from "lucide-react";

// Fiche prospect › Onboarding: the roadmap by phase, each module's status and actual hours.
export default function OnboardingTab({ roadmap, onboarding, emptyState, doneCount, totalCount, pct, canManage, onRepair, onToggleModule, onActualHours }) {
  const inconsistent = emptyState === "repair";
  return (
    <>
      {roadmap.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-500 space-y-3" data-testid={`onboarding-empty-${emptyState}`}>
          {emptyState === "no_history" ? (
            <>
              <p className="font-medium text-slate-700">Historique d'onboarding non enregistré</p>
              <p>Cette pharmacie est cliente active ; aucun suivi d'onboarding n'a été saisi dans le CRM.</p>
            </>
          ) : (
            <>
              <p>
                {inconsistent
                  ? "Feuille de route introuvable pour ce prospect. Ce statut a probablement été défini sans passer par \"Lancer l'onboarding\" (import CSV ou donnée historique). Après réparation, renseignez les étapes déjà réalisées."
                  : "Feuille de route non générée. Passez le prospect en \"Gagné\" puis lancez l'onboarding pour l'initialiser."}
              </p>
              {inconsistent && canManage ? (
                <Button
                  onClick={onRepair}
                  data-testid="btn-repair-onboarding"
                >
                  <Rocket size={14} className="mr-1.5" /> Réparer la feuille de route
                </Button>
              ) : inconsistent ? (
                <p className="text-xs text-slate-400">Contactez un administrateur pour la régénérer.</p>
              ) : null}
            </>
          )}
        </Card>
      ) : (
        <>
          <Card className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm font-medium flex items-center gap-2">
                  Avancement global
                  {onboarding?.mode && <Badge variant="outline">{onboarding.mode === "presentiel" ? "Présentiel" : "Visio"}</Badge>}
                </div>
                <div className="text-xs text-slate-500">{doneCount} / {totalCount} modules terminés</div>
              </div>
              <div className="font-display font-bold text-2xl text-jamora">{pct}%</div>
            </div>
            <Progress value={pct} className="h-2" />
          </Card>
          {roadmap.map((ph) => (
            <Card key={ph.id} className="p-4">
              <h4 className="font-display font-semibold text-slate-900 mb-3">
                {ph.name}
              </h4>
              <ul className="space-y-2">
                {ph.modules.map((m) => {
                  const icon = m.status === "done" ? (
                    <CheckCircle2 className="text-jamora-light" size={20} />
                  ) : m.status === "in_progress" ? (
                    <Play className="text-jamora" size={20} />
                  ) : (
                    <Circle className="text-slate-300" size={20} />
                  );
                  const statusLabel = m.status === "done" ? "Terminé" : m.status === "in_progress" ? "En cours" : "À faire";
                  const statusColor = m.status === "done"
                    ? "bg-jamora/10 text-jamora"
                    : m.status === "in_progress"
                      ? "bg-jamora text-white"
                      : "bg-slate-100 text-slate-500";
                  return (
                    <li key={m.id} data-testid={`module-${m.id}`} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50">
                      {canManage ? (
                        <button
                          onClick={() => onToggleModule(ph.id, m.id, m.status)}
                          data-testid={`btn-toggle-module-${m.id}`}
                          className="flex-shrink-0"
                        >
                          {icon}
                        </button>
                      ) : (
                        <span className="flex-shrink-0">{icon}</span>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-sm ${m.status === "done" ? "line-through text-slate-400" : "text-slate-900"}`}>
                            {m.name}
                          </span>
                          <span className={`text-2xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${statusColor}`}>
                            {statusLabel}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500">
                          Estimé {m.estimated_hours}h · Réel {m.actual_hours}h
                        </div>
                      </div>
                      {canManage && (
                        <Input
                          type="number"
                          step="0.5"
                          min="0"
                          defaultValue={m.actual_hours}
                          onBlur={(e) => onActualHours(ph.id, m.id, e.target.value)}
                          className="w-20 h-8 text-xs"
                          data-testid={`input-actual-${m.id}`}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </Card>
          ))}
        </>
      )}
    </>
  );
}
